/**
 * ChangePasswordDialog.jsx — modal for an authenticated user to rotate
 * their own password. Calls POST /api/auth/change-password with
 * current_password + new_password + confirm_password.
 */
import React, { useState } from 'react';
import { Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatApiError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
    setShowCurrent(false); setShowNext(false);
  };

  const close = (v) => {
    if (!submitting) {
      onOpenChange(v);
      if (!v) reset();
    }
  };

  // Client-side guards (the backend re-validates these too).
  const tooShort = next.length > 0 && next.length < 8;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const canSubmit =
    !!current && !!next && !!confirm &&
    !tooShort && !mismatch && !sameAsCurrent && !submitting;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: current,
          new_password: next,
          confirm_password: confirm,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(data.detail) || `HTTP ${r.status}`);
      toast.success('Password changed');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Could not change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-indigo-400" /> Change Password
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Enter your current password, then choose a new one. Minimum 8 characters.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Current password</label>
            <div className="relative">
              <Input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="bg-slate-800 border-slate-700 pr-9"
                autoComplete="current-password"
                data-testid="change-password-current"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                tabIndex={-1}
                aria-label={showCurrent ? 'Hide current password' : 'Show current password'}
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">New password</label>
            <div className="relative">
              <Input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                className="bg-slate-800 border-slate-700 pr-9"
                autoComplete="new-password"
                data-testid="change-password-new"
                required
              />
              <button
                type="button"
                onClick={() => setShowNext((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                tabIndex={-1}
                aria-label={showNext ? 'Hide new password' : 'Show new password'}
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {tooShort && <p className="text-xs text-rose-400 mt-1">Use at least 8 characters.</p>}
            {sameAsCurrent && <p className="text-xs text-rose-400 mt-1">Pick something different from your current password.</p>}
          </div>

          <div>
            <label className="text-xs text-slate-300 mb-1 block">Confirm new password</label>
            <Input
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-slate-800 border-slate-700"
              autoComplete="new-password"
              data-testid="change-password-confirm"
              required
            />
            {mismatch && <p className="text-xs text-rose-400 mt-1">Passwords don't match.</p>}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => close(false)}
              disabled={submitting}
              className="border-slate-600 text-slate-200"
              data-testid="change-password-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="change-password-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Update password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
